import { FitFinderUI } from "./FitFinderUI";
import { StandardProps } from "@/components/componentMap";
import { useVariant } from "@/hooks/useVariant/useVariant.ts";
import {
  FitFinderImpressions,
  getBreadCrumbs,
  includedCategories,
  isFeatureEnabledForCategory,
  normalizeImageUrl,
} from "./FitFinderUtils";

import { recordImpressionByParam } from "@JohnLewisPartnership/experiments-utils";
import { useEffect } from "react";
import { useMonetateId } from "@/hooks/useMonetateId/useMonetateId.ts";

export function FitFinder({
  product,
  runtimeConfig,
  experiencesRawObject,
}: Pick<StandardProps, "product" | "runtimeConfig" | "experiencesRawObject">) {
  const { variant } = useVariant(product);
  const { monetateId } = useMonetateId();
  const breadCrumbs = getBreadCrumbs(product);

  const braCategory = breadCrumbs?.find(
    (category) => category.value?.toLowerCase()?.indexOf("bras") > -1,
  );

  const isFeatureEnabled = isFeatureEnabledForCategory(
    breadCrumbs,
    includedCategories,
  );

  const handleRecordImpression = (state: string, impressionStatus: boolean) => {
    recordImpressionByParam(
      runtimeConfig?.monetateEnvironment,
      experiencesRawObject as [],
      state,
      impressionStatus.toString(),
      monetateId,
    );
  };

  useEffect(() => {
    handleRecordImpression(FitFinderImpressions.available, false);
    handleRecordImpression(FitFinderImpressions.opened, false);
  }, []);

  if (
    braCategory ||
    product.__typename !== "SimpleProduct" ||
    !isFeatureEnabled
  )
    return null;

  const { skuId } = variant.aliases;
  const brand = product.brand?.name || "";
  const alternatives = variant.images.alternatives || [];
  const mainThumbNail =
    [variant.images.primary, ...alternatives]
      .filter(Boolean)
      .map(normalizeImageUrl)[0] || "";
  const currentPrice = variant.price?.value || "";
  const pimProductId = product.aliases?.pimProductId;
  const currentColor =
    variant.differentiators?.colour || variant.colour?.trueColour || "";

  const currentDifferentiatorsColour = variant.differentiators?.colour || null;

  const sizes = product.variants
    .filter(
      (variant) =>
        variant.differentiators?.size &&
        variant.differentiators?.colour === currentDifferentiatorsColour,
    )
    .map((variant) => ({
      value: variant.differentiators?.size || "",
      isAvailable: variant.availability.availableToOrder,
    }));

  const skuTitle = variant.differentiators?.colour
    ? product.title + ", " + variant.differentiators.colour
    : product.title;

  const fitFinderUrl = runtimeConfig.fitFinderUrl;

  return (
    <FitFinderUI
      mainThumbnail={mainThumbNail}
      currentColour={currentColor.trim()}
      sizes={sizes}
      brand={brand}
      price={currentPrice}
      crumbs={breadCrumbs}
      skuTitle={skuTitle}
      productId={pimProductId}
      currentSkuId={skuId}
      fitFinderUrl={fitFinderUrl}
      handleRecordImpression={handleRecordImpression}
    />
  );
}
